import { createAuthClient } from '@augment-toolkit/auth-client';
import { config } from '../config';

// One client for the app's lifetime. `oidc-client-ts` keeps the silent-renew
// timer and the session on the instance, so a second one would race the first
// over the same sessionStorage keys.

// Registered rather than passed in: the handler needs to dispatch and the store
// imports this module, so the other direction is a cycle. store/index.ts calls
// this once, at creation.
let sessionEndedHandler: (() => void) | undefined;

export const setSessionEndedHandler = (handler: () => void): void => {
  sessionEndedHandler = handler;
};

export const authClient = createAuthClient({
  authority: config.auth.authority,
  clientId: config.auth.clientId,
  redirectUri: config.auth.redirectUri,
  postLogoutRedirectUri: config.auth.postLogoutRedirectUri,

  // Cognito's logout is not RP-initiated logout. It lives on the hosted UI
  // domain and takes `client_id` and `logout_uri`, so the standard parameters
  // are ignored and `signoutRedirect()` would leave the user logged in upstream.
  //
  // `logout_uri` must exactly match a Logout URL on the app client. It resolves
  // to <origin>/login, and the polaris client registers localhost:3000-3004
  // alongside the deployed origin.
  logoutUrl: ({ clientId, postLogoutRedirectUri }) =>
    `https://${config.auth.hostedUiDomain}/logout` +
    `?client_id=${encodeURIComponent(clientId)}` +
    `&logout_uri=${encodeURIComponent(postLogoutRedirectUri)}`,

  // Both mean the same thing to the app: no usable token. Unhandled, they leave
  // a user who looks signed in while every request comes back 401.
  onSilentRenewError: () => sessionEndedHandler?.(),
  onAccessTokenExpired: () => sessionEndedHandler?.(),
});
